import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { User, Shield, LogOut, ChevronDown, ChevronUp, Plus, CheckCircle, Users, KeyRound, Mail, Tag, Package, FileText } from 'lucide-react';
import api from '../api';
import Avatar from './Avatar';
import Spinner from './Spinner';
import BrandLogo from './BrandLogo';

const Sidebar = ({ user, sessions }) => {
    const [isAccountSelectorOpen, setIsAccountSelectorOpen] = useState(false);
    // Переключение аккаунта - запрос к серверу, а не мгновенное действие. Пока он идёт,
    // список заблокирован: повторное нажатие начинало вход заново поверх незаконченного.
    const [switchingId, setSwitchingId] = useState(null);
    const accountSelectorRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (accountSelectorRef.current && !accountSelectorRef.current.contains(event.target)) {
                setIsAccountSelectorOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSwitchAccount = (id) => {
        if (switchingId !== null) return;
        setSwitchingId(id);

        api.get(`/api/auth/switch/${id}`)
            .then(res => {
                if (res.data && res.data.targetUrl) {
                    window.location.href = res.data.targetUrl;
                } else {
                    window.location.href = '/dashboard/profile';
                }
            })
            .catch(() => {
                setSwitchingId(null);
                alert('Не удалось переключить аккаунт');
                window.location.reload();
            });
    };

    const isActive = (path) => location.pathname.includes(path);

    if (!user) return null;

    return (
        <aside className="portal-sidebar order-1 w-full lg:w-[280px] bg-white border-b lg:border-b-0 lg:border-r border-gray-200 flex flex-col justify-between shrink-0 lg:overflow-y-auto">
            <div>
                <Link to="/dashboard/profile" aria-label="IT-Burger — профиль" className="block px-6 pt-6 no-underline">
                    <BrandLogo align="start" />
                </Link>
                <div className="flex flex-col p-6 gap-2">
                    <Link
                        to="/dashboard/profile"
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border-none cursor-pointer text-[15px] font-medium transition-all text-left no-underline ${isActive('profile') ? 'bg-blue-50 text-blue-600' : 'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
                    >
                        <User size={20} />
                        <span>Профиль</span>
                    </Link>
                    <Link
                        to="/dashboard/security"
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border-none cursor-pointer text-[15px] font-medium transition-all text-left no-underline ${isActive('security') ? 'bg-blue-50 text-blue-600' : 'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
                    >
                        <Shield size={20} />
                        <span>Безопасность</span>
                    </Link>

                    {user.roles && user.roles.includes('ROLE_ADMIN') && (
                        <>
                            <div className="mt-4 mb-1 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Администрирование</div>
                            <Link to="/dashboard/admin/products"
                                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-[15px] font-medium no-underline ${isActive('admin/products') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}>
                                <Package size={20} /> Продукты
                            </Link>
                            <Link
                                to="/dashboard/admin/users"
                                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border-none cursor-pointer text-[15px] font-medium transition-all text-left no-underline ${isActive('admin/users') ? 'bg-blue-50 text-blue-600' : 'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
                            >
                                <Users size={20} />
                                <span>Пользователи</span>
                            </Link>
                            <Link
                                to="/dashboard/admin/mail"
                                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border-none cursor-pointer text-[15px] font-medium transition-all text-left no-underline ${isActive('admin/mail') ? 'bg-blue-50 text-blue-600' : 'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
                            >
                                <Mail size={20} />
                                <span>Почта</span>
                            </Link>
                            <Link
                                to="/dashboard/admin/tags"
                                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border-none cursor-pointer text-[15px] font-medium transition-all text-left no-underline ${isActive('admin/tags') ? 'bg-blue-50 text-blue-600' : 'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
                            >
                                <Tag size={20} />
                                <span>Метки</span>
                            </Link>
                            <Link
                                to="/dashboard/admin/clients"
                                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border-none cursor-pointer text-[15px] font-medium transition-all text-left no-underline ${isActive('admin/clients') ? 'bg-blue-50 text-blue-600' : 'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
                            >
                                <KeyRound size={20} />
                                <span>OAuth2-клиенты</span>
                            </Link>
                            <Link to="/dashboard/admin/agreements" className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-[15px] font-medium no-underline ${isActive('admin/agreements') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}>
                                <FileText size={20} /><span>Соглашения</span>
                            </Link>
                        </>
                    )}
                </div>
            </div>

            <div className="p-5 border-t border-gray-200 relative" ref={accountSelectorRef}>
                <button
                    className="flex items-center gap-3 w-full p-3 border border-gray-200 rounded-lg bg-white cursor-pointer transition-all hover:bg-gray-50 hover:border-gray-300 text-left"
                    onClick={() => setIsAccountSelectorOpen(!isAccountSelectorOpen)}
                >
                    <Avatar src={user.avatarUrl} name={user.name} email={user.email} size={36} />
                    <div className="flex flex-col overflow-hidden flex-1">
                        <span className="text-sm font-semibold text-gray-900 truncate">{user.name || 'Без имени'}</span>
                        <span className="text-xs text-gray-500 truncate">{user.email}</span>
                    </div>
                    {isAccountSelectorOpen ? <ChevronUp size={16} className="ml-auto text-gray-400" /> : <ChevronDown size={16} className="ml-auto text-gray-400" />}
                </button>

                {isAccountSelectorOpen && (
                    <div className="absolute bottom-[calc(100%+10px)] left-5 right-5 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-10 flex flex-col">
                        {sessions.map(session => {
                            const isSwitching = switchingId === session.id;
                            const busy = switchingId !== null;
                            return (
                                <button
                                    key={session.id}
                                    disabled={busy}
                                    aria-busy={isSwitching}
                                    className={`flex items-center gap-3 px-4 py-3 transition-all border-b border-gray-100 bg-white text-left last:border-b-0
                                        ${session.isCurrent ? 'bg-gray-50 cursor-default' : ''}
                                        ${busy && !isSwitching ? 'opacity-40' : ''}
                                        ${busy ? 'cursor-default' : 'cursor-pointer hover:bg-gray-50'}`}
                                    onClick={() => {
                                        if (!session.isCurrent) handleSwitchAccount(session.id);
                                        else setIsAccountSelectorOpen(false);
                                    }}
                                >
                                    <Avatar src={session.avatarUrl} name={session.name} email={session.email} size={36} />
                                    <div className="flex flex-col overflow-hidden flex-1">
                                        <span className="text-sm font-semibold text-gray-900 truncate">{session.name || 'Без имени'} {session.isCurrent && '(Вы)'}</span>
                                        <span className="text-xs text-gray-500 truncate">
                                            {isSwitching ? 'Переключаем…' : session.email}
                                        </span>
                                    </div>
                                    {isSwitching && <Spinner size={16} className="text-blue-600 shrink-0" />}
                                    {!isSwitching && session.isCurrent && <CheckCircle size={16} className="text-blue-600 shrink-0" />}
                                </button>
                            );
                        })}
                        <button
                            disabled={switchingId !== null}
                            className="flex items-center justify-center gap-3 px-4 py-3 transition-colors border-b border-gray-100 text-left last:border-b-0 text-blue-600 font-medium bg-gray-50 enabled:cursor-pointer enabled:hover:bg-blue-50 disabled:opacity-40 disabled:cursor-default"
                            onClick={() => navigate('/login')}
                        >
                            <Plus size={16} />
                            <span>Добавить аккаунт</span>
                        </button>
                        <button
                            disabled={switchingId !== null}
                            className="flex items-center gap-3 px-4 py-3 transition-colors border-b border-gray-100 bg-white text-left last:border-b-0 text-red-500 enabled:cursor-pointer enabled:hover:bg-gray-50 disabled:opacity-40 disabled:cursor-default"
                            onClick={() => navigate('/logout')}
                        >
                            <LogOut size={16} />
                            <span>Выйти на этом устройстве</span>
                        </button>
                    </div>
                )}
            </div>
        </aside>
    );
};

export default Sidebar;
